// Phase 2 drawers (prototype drawerHTML 'park' and 'xfer'): Park (P) and Send to… (X). Phase 4: the Park drawer's AI
// watches (api-phase4.md: POST /park with watches).

import { api, PARK_REASONS, type ParkReason, type Presence } from '../api.ts';
import { Icon } from '../components/Icon.tsx';
import { ErrorBox, Loading } from '../components/ui.tsx';
import { useLoad } from '../hooks.ts';
import { GroupOptions, OperatorOptions, useTransferGroups } from '../live/people.tsx';
import { usePresence } from '../live/presence.tsx';
import type { Ctl } from './ctl.ts';
import { CONDITION_OPTIONS } from './ai.ts';
import { useDraft } from './drafts.ts';
import { CameraChips, WatchFields, watchInputs } from './Watches.tsx';
import { Drawer } from './Drawers.tsx';

export const FALLBACK_MINUTES = [5, 10, 15, 30, 60] as const;

/* ---------- park ---------- */

/** At most this many watches per park (api-phase4.md). */
const MAX_WATCHES = 4;

/**
 * AI park watches (prototype drawerHTML 'park', api-phase4.md): raise this event back to me when the AI sees police,
 * the subject coming back, any person or vehicle, the gate, or a custom condition, on the cameras I pick (alarm
 * cameras first). Watch time defaults to the fallback timer; an optional minimum dwell ignores people passing through.
 */
function ParkWatchSection({ c, w, set, fallback }: { c: Ctl; w: ParkWatchDraft; set: (p: Partial<ParkWatchDraft>) => void; fallback: number }) {
  const ev = c.ev;
  const alarmCams = new Set(ev.alarms.map((a) => a.cameraId).filter((x): x is string => !!x));
  const toggle = (v: string) => set({ conditions: w.conditions.includes(v) ? w.conditions.filter((x) => x !== v) : [...w.conditions, v].slice(-MAX_WATCHES), touched: true });
  return (
    <div className="sec">
      <div className="lbl ai-t">
        <span>
          <Icon name="spark" /> AI watch · raise this event back to me when it sees
        </span>
      </div>
      <div className="optlist" role="group" aria-label="Watch for">
        {CONDITION_OPTIONS.map((o) => {
          const on = w.conditions.includes(o.value);
          return (
            <button key={o.value} type="button" role="checkbox" aria-checked={on} className={`opt aio${on ? ' on' : ''}`} onClick={() => toggle(o.value)}>
              <span className="cb" aria-hidden="true">
                {on && <Icon name="check" />}
              </span>
              <span>
                {o.label}
                <small>{o.sub}</small>
              </span>
              <span />
            </button>
          );
        })}
      </div>
      {w.conditions.length > 0 && (
        <>
          <div className="lbl">Cameras to watch</div>
          <CameraChips cams={ev.cameras} alarmCamIds={alarmCams} value={w.cams} onChange={(v) => set({ cams: v, touched: true })} label="Cameras to watch" />
          {w.tried && !w.cams.length && <span className="inline-err">Pick at least one camera to watch.</span>}
          <WatchFields
            conditions={w.conditions}
            minutes={w.minutes}
            setMinutes={(m) => set({ minutes: m })}
            untilLabel={`Until the fallback timer (${fallback} min)`}
            dwell={w.dwell}
            setDwell={(n) => set({ dwell: n })}
            description={w.description}
            setDescription={(s) => set({ description: s })}
            gate={w.gate}
            setGate={(g) => set({ gate: g })}
            tried={w.tried}
          />
          <div className="note ai-t">
            {w.conditions.some((x) => x === 'police_arrival' || x === 'subject_return' || x === 'custom')
              ? 'Where the agent acts live (Level 1+), it checks each hit on the frame before it raises; a hit it does not answer in 20 s raises anyway.'
              : 'A hit raises the event back to you at once, with the frame.'}
          </div>
        </>
      )}
      {w.conditions.length === 0 && <div className="note">No AI watch: only the fallback timer and new alarms raise it.</div>}
    </div>
  );
}

interface ParkWatchDraft {
  conditions: string[];
  cams: string[];
  minutes: number | null;
  dwell: number;
  description: string;
  gate: 'open' | 'closed';
  /** The operator changed the watch: the reason no longer picks it. */
  touched: boolean;
  tried: boolean;
}

export function ParkDrawer({ c, onClose }: { c: Ctl; onClose: () => void }) {
  const ev = c.ev;
  const presence = usePresence();
  const dispatched = ev.actions.some((a) => a.type === 'dispatch' && a.status === 'succeeded');
  const [reason, setReason] = useDraft<ParkReason>(c.drafts, ev.id, 'park.reason', dispatched ? 'police_dispatched' : 'monitoring');
  const [fallback, setFallback] = useDraft<number>(c.drafts, ev.id, 'park.fallback', 15);
  const [note, setNote] = useDraft(c.drafts, ev.id, 'park.note', '');
  // Alarm cameras that are online, in alarm order.
  const alarmCamIds = [...new Set(ev.alarms.map((a) => a.cameraId).filter((x): x is string => !!x))].filter((id) => ev.cameras.find((x) => x.id === id)?.health?.status !== 'offline');
  // Police dispatched: a police-arrival watch on the alarm cameras is picked for you (prototype parkDraft).
  const policeWatch = (r: ParkReason): Pick<ParkWatchDraft, 'conditions' | 'cams'> =>
    r === 'police_dispatched' ? { conditions: ['police_arrival'], cams: alarmCamIds.slice(0, 4) } : { conditions: [], cams: alarmCamIds.slice(0, 4) };
  const [w, setW] = useDraft<ParkWatchDraft>(c.drafts, ev.id, 'park.watch', () => ({
    ...policeWatch(dispatched ? 'police_dispatched' : 'monitoring'),
    minutes: null,
    dwell: 0,
    description: '',
    gate: 'open',
    touched: false,
    tried: false,
  }));
  const setWatch = (p: Partial<ParkWatchDraft>) => setW((o) => ({ ...o, ...p }));
  const pickReason = (r: ParkReason) => {
    setReason(r);
    if (!w.touched) setWatch(policeWatch(r));
  };
  const primary = presence?.me?.groups.find((g) => g.primary)?.name ?? null;
  const watchOn = c.features.agent && w.conditions.length > 0;
  const watchValid = !watchOn || (w.cams.length > 0 && (!w.conditions.includes('custom') || !!w.description.trim()));

  const submit = async () => {
    setWatch({ tried: true });
    if (!watchValid) return;
    const watches = watchOn ? watchInputs({ conditions: w.conditions, cameraIds: w.cams, minutes: w.minutes, dwell: w.dwell, description: w.description, gate: w.gate }) : [];
    const r = await c.run(
      'Park',
      (v) => api.park(ev.id, { reason, fallbackMinutes: fallback, ...(note.trim() ? { note: note.trim() } : {}), ...(watches.length ? { watches } : {}) }, v),
      (d) => d,
    );
    if (r) {
      c.drafts.clear(ev.id, 'park.');
      onClose();
      c.afterLeave(ev.id);
    }
  };

  return (
    <Drawer
      icon="park"
      title="Park event"
      sub="Frees a slot. The event stays yours."
      conflict={c.conflict}
      onClose={onClose}
      onSubmit={() => void submit()}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn pri" disabled={!!c.busy}>
            <Icon name="park" />
            {c.busy === 'Park' ? 'Parking…' : 'Park'}
          </button>
        </>
      }
    >
      <div className="sec">
        <div className="lbl">Reason</div>
        <div role="radiogroup" aria-label="Park reason" className="optlist">
          {PARK_REASONS.map((r, i) => (
            <button
              key={r.value}
              type="button"
              role="radio"
              aria-checked={reason === r.value}
              className={`opt${reason === r.value ? ' on' : ''}`}
              onClick={() => pickReason(r.value)}
              data-autofocus={reason === r.value || (i === 0 && !PARK_REASONS.some((x) => x.value === reason)) ? true : undefined}
            >
              <span className="rd" aria-hidden="true" />
              <span>{r.label}</span>
              <span />
            </button>
          ))}
        </div>
      </div>
      {c.features.agent && <ParkWatchSection c={c} w={w} set={setWatch} fallback={fallback} />}
      <div className="row2">
        <label className="field">
          <span className="lbl">Fallback timer</span>
          <select value={fallback} onChange={(e) => setFallback(Number(e.target.value))}>
            {FALLBACK_MINUTES.map((m) => (
              <option key={m} value={m}>
                Raise after {m} min
              </option>
            ))}
          </select>
        </label>
        <div className="field">
          <span className="lbl">If I'm unavailable</span>
          <div className="note fg">
            It goes to {primary ? <b>{primary}</b> : 'your primary group'}, then Supervisors if nobody takes it within 60 s.
          </div>
        </div>
      </div>
      <label className="field">
        <span className="lbl">Note (optional)</span>
        <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. SPD unit en route, callback promised by 02:30" maxLength={1000} />
      </label>
      <div className="note">
        It comes back to you (and pulses in your tabs) when the timer ends, a new alarm joins it, or its severity rises. Parked events do not count toward your{' '}
        {c.maxActive}-event cap.
      </div>
    </Drawer>
  );
}

/* ---------- send to ---------- */

type Target = { kind: 'user' | 'group'; id: string } | null;

export function SendToDrawer({ c, onClose }: { c: Ctl; onClose: () => void }) {
  const ev = c.ev;
  const people = useLoad((sig) => api.presenceAll(sig), []);
  const others: Presence[] = (people.data ?? []).filter((p) => p.userId !== c.meId);
  const { groups, error: groupsErr } = useTransferGroups(people.data);
  const [mode, setMode] = useDraft<'send' | 'reassign'>(c.drafts, ev.id, 'xfer.mode', 'send');
  const [target, setTarget] = useDraft<Target>(c.drafts, ev.id, 'xfer.target', null);
  const [note, setNote] = useDraft(c.drafts, ev.id, 'xfer.note', '');
  const [tried, setTried] = useDraft(c.drafts, ev.id, 'xfer.tried', false);
  const reassign = c.isSup && mode === 'reassign';
  const name =
    target?.kind === 'user' ? (others.find((p) => p.userId === target.id)?.displayName ?? 'operator') : target ? (groups?.find((g) => g.id === target.id)?.name ?? 'group') : '';
  const valid = !!target && (reassign ? target.kind === 'user' : !!note.trim());

  const submit = async () => {
    setTried(true);
    if (!valid || !target) return;
    const r = reassign
      ? await c.run('Reassign', (v) => api.reassign(ev.id, target.id, v), () => null)
      : await c.run(
          'Send',
          (v) => api.transfer(ev.id, target.kind === 'user' ? { toUserId: target.id, note: note.trim() } : { toGroupId: target.id, note: note.trim() }, v),
          () => null,
        );
    if (r) {
      c.drafts.clear(ev.id);
      onClose();
      c.afterLeave(ev.id);
    }
  };

  return (
    <Drawer
      icon="send"
      title={reassign ? 'Reassign event' : 'Send event'}
      sub={reassign ? 'Makes it their active event now, even over their cap.' : 'The receiver gets the full history and your note.'}
      conflict={c.conflict}
      onClose={onClose}
      onSubmit={() => void submit()}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn pri" disabled={!!c.busy || (tried && !valid)}>
            <Icon name={reassign ? 'users' : 'send'} />
            {c.busy ? 'Sending…' : reassign ? 'Reassign' : 'Send'}
          </button>
        </>
      }
    >
      {c.isSup && (
        <div className="seg full" role="radiogroup" aria-label="Send or reassign">
          <button
            type="button"
            role="radio"
            aria-checked={mode === 'send'}
            className={mode === 'send' ? 'on' : ''}
            onClick={() => setMode('send')}
          >
            <Icon name="send" />
            Send to
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={mode === 'reassign'}
            className={mode === 'reassign' ? 'on' : ''}
            onClick={() => {
              setMode('reassign');
              if (target?.kind === 'group') setTarget(null);
            }}
          >
            <Icon name="users" />
            Reassign (supervisor)
          </button>
        </div>
      )}
      <div className="sec">
        <div className="lbl">Operators</div>
        {people.error && !people.data ? (
          <ErrorBox error={people.error} onRetry={people.reload} title="Could not load operators" />
        ) : !people.data ? (
          <Loading label="Loading operators…" />
        ) : (
          <OperatorOptions list={others} selected={target?.kind === 'user' ? target.id : null} onSelect={(id) => setTarget({ kind: 'user', id })} overCapOk={reassign} />
        )}
      </div>
      {!reassign && (
        <div className="sec">
          <div className="lbl">Groups</div>
          {groupsErr && !groups ? (
            <ErrorBox error={groupsErr} title="Could not load groups" />
          ) : !groups ? (
            <Loading label="Loading groups…" />
          ) : (
            <GroupOptions groups={groups} people={people.data ?? []} selected={target?.kind === 'group' ? target.id : null} onSelect={(id) => setTarget({ kind: 'group', id })} />
          )}
        </div>
      )}
      {!reassign && (
        <label className="field">
          <span className="lbl">Note (required)</span>
          <textarea
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="What does the receiver need to know?"
            aria-invalid={tried && !note.trim()}
            maxLength={1000}
          />
          {tried && !note.trim() && <span className="inline-err">Add a note for the receiver.</span>}
        </label>
      )}
      {tried && !target && <span className="inline-err">Pick {reassign ? 'an operator' : 'an operator or a group'}.</span>}
    </Drawer>
  );
}
